import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Phone, Mail, Building2, Trash2, Edit2, MoreVertical, Download, Upload, AlertCircle } from 'lucide-react';
import { exportContacts } from '../utils/export';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../services/api';

const emptyForm = {
  first_name: '', last_name: '', email: '', phone: '',
  company_id: '', company_name: '', tags: [], notes: ''
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportResult, setShowImportResult] = useState(false);
  const importRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get('/contacts'), api.get('/companies')])
      .then(([c, co]) => { setContacts(c.data); setCompanies(co.data); })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = contacts.filter(c =>
    !search || `${c.first_name} ${c.last_name} ${c.email} ${c.company_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => {
    setEditingContact(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditingContact(c);
    setForm({
      first_name: c.first_name || '', last_name: c.last_name || '',
      email: c.email || '', phone: c.phone || '',
      company_id: c.company_id || '', company_name: c.company_name || '',
      tags: c.tags || [], notes: c.notes || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name) { toast.error('Prénom et nom requis'); return; }
    setSaving(true);
    try {
      if (editingContact) {
        const res = await api.put(`/contacts/${editingContact.id}`, form);
        setContacts(prev => prev.map(c => c.id === editingContact.id ? res.data : c));
        toast.success('Contact mis à jour');
      } else {
        const res = await api.post('/contacts', form);
        setContacts(prev => [res.data, ...prev]);
        toast.success('Contact créé');
      }
      setShowDialog(false);
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Contact supprimé');
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    try {
      const res = await api.post('/contacts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { created, skipped, errors } = res.data;
      setImportResult({ created, skipped, errors });
      setShowImportResult(true);
      if (created > 0) {
        const updated = await api.get('/contacts');
        setContacts(updated.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    }
  };

  const getInitials = (c) => `${c.first_name[0] || ''}${c.last_name[0] || ''}`.toUpperCase();

  const avatarColors = ['bg-[#6366F1]/20 text-[#6366F1]', 'bg-[#3B82F6]/20 text-[#3B82F6]', 'bg-[#1B99E5]/20 text-[#1B99E5]', 'bg-[#F97316]/20 text-[#F97316]', 'bg-[#6366F1]/20 text-[#6366F1]'];
  const getAvatarColor = (id) => avatarColors[id.charCodeAt(0) % avatarColors.length];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contacts
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <input ref={importRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleImportCSV} data-testid="import-csv-input" />
          <Button onClick={() => importRef.current?.click()} variant="outline" className="h-9 px-3 text-sm border-gray-200 text-gray-600 hover:bg-gray-50" disabled={importing} data-testid="import-contacts-btn">
            {importing ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600 mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
            {importing ? 'Import...' : 'Importer CSV'}
          </Button>
          <Button onClick={() => exportContacts(filtered)} variant="outline" className="h-9 px-3 text-sm border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="export-contacts-btn">
            <Download size={14} className="mr-1.5" /> Exporter CSV
          </Button>
          <Button onClick={openAdd} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-4 text-sm font-medium rounded-lg active:scale-95"
            style={{ transition: 'background-color 150ms ease, transform 100ms ease' }} data-testid="add-contact-btn">
            <Plus size={15} className="mr-1.5" /> Nouveau contact
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 h-9 text-sm border-gray-200 bg-white dark:bg-[#1A1D23]"
          data-testid="contacts-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
              <Building2 size={20} className="text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aucun contact</p>
            <p className="text-xs text-gray-400 mt-1">Créez votre premier contact</p>
            <Button onClick={openAdd} size="sm" className="mt-3 bg-[#1B99E5] hover:bg-[#0f86cc] text-white">
              <Plus size={13} className="mr-1.5" /> Ajouter
            </Button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Téléphone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Entreprise</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 h-12"
                  style={{ transition: 'background-color 100ms ease' }} data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className={`text-xs font-bold ${getAvatarColor(c.id)}`}>{getInitials(c)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{c.first_name} {c.last_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {c.email && <a href={`mailto:${c.email}`} className="text-sm text-gray-600 dark:text-gray-400 hover:text-[#1B99E5] flex items-center gap-1.5"
                      style={{ transition: 'color 150ms ease' }}>
                      <Mail size={12} />{c.email}
                    </a>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {c.phone && <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                      <Phone size={12} />{c.phone}
                    </span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {c.company_name && <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                      <Building2 size={12} />{c.company_name}
                    </span>}
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700" data-testid={`contact-actions-${c.id}`}>
                          <MoreVertical size={14} className="text-gray-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}>
                          <Edit2 size={13} className="mr-2" /> Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-600 focus:text-red-600" data-testid={`delete-contact-${c.id}`}>
                          <Trash2 size={13} className="mr-2" /> Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingContact ? 'Modifier le contact' : 'Nouveau contact'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Prénom *</Label>
                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="contact-firstname-input" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Nom *</Label>
                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="contact-lastname-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Téléphone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Entreprise</Label>
              <Select value={form.company_id} onValueChange={v => {
                const co = companies.find(c => c.id === v);
                setForm({ ...form, company_id: v, company_name: co?.name || '' });
              }}>
                <SelectTrigger><SelectValue placeholder="Sélectionner une entreprise" /></SelectTrigger>
                <SelectContent>
                  {companies.map(co => <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white" data-testid="save-contact-button">
              {saving ? 'Sauvegarde...' : editingContact ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Import Result Dialog */}
      <Dialog open={showImportResult} onOpenChange={setShowImportResult}>
        <DialogContent className="sm:max-w-sm" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Résultat de l'import</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="flex items-center justify-around">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1B99E5]" style={{ fontFamily: 'Manrope, sans-serif' }}>{importResult?.created || 0}</p>
                <p className="text-xs text-gray-500">importés</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#FFAB00]" style={{ fontFamily: 'Manrope, sans-serif' }}>{importResult?.skipped || 0}</p>
                <p className="text-xs text-gray-500">ignorés (doublon)</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#EF4444]" style={{ fontFamily: 'Manrope, sans-serif' }}>{importResult?.errors?.length || 0}</p>
                <p className="text-xs text-gray-500">erreurs</p>
              </div>
            </div>
            {importResult?.errors?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 max-h-32 overflow-y-auto">
                {importResult.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1"><AlertCircle size={11} className="mt-0.5 flex-shrink-0" />{e}</p>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400">Format attendu: CSV avec colonnes <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">prenom;nom;email;telephone;entreprise</code></p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowImportResult(false)} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
